import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import Discovery from "@/components/matcher/Discovery";

const HERO_IMG =
  "https://images.pexels.com/photos/36779953/pexels-photo-36779953.jpeg";

export default function Hero({ onStart }) {
  return (
    <section
      className="relative min-h-screen w-full grid grid-cols-1 lg:grid-cols-12"
      data-testid="hero-section"
    >
      {/* Left column — editorial copy */}
      <div className="lg:col-span-7 flex flex-col justify-between px-6 sm:px-12 lg:px-20 py-12 lg:py-20 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
          className="flex items-center gap-4"
        >
          <span className="w-10 h-px bg-foreground/60" />
          <span className="label-caps text-foreground/60">
            Scent Match · Volume I
          </span>
        </motion.div>

        <div className="mt-24 lg:mt-0">
          <motion.h1
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.1, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
            className="font-serif text-5xl sm:text-7xl lg:text-[7.5rem] leading-[0.9] tracking-tight text-foreground"
          >
            Find the
            <br />
            <em className="italic text-foreground/90">fragrance</em>
            <br />
            that is
            <br />
            already
            <br />
            <span style={{ color: "hsl(var(--gold))" }}>yours.</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 0.6 }}
            className="mt-10 max-w-xl text-base sm:text-lg text-foreground/70 leading-relaxed"
          >
            An intimate matchmaker for the fragrance-obsessed. Tell us the notes
            you love, your budget, the moment, and the weather — a discerning
            nose distills three perfumes made for you.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.9 }}
            className="mt-12 flex flex-wrap items-center gap-6"
          >
            <button
              className="btn-sharp gold"
              onClick={onStart}
              data-testid="hero-start-btn"
            >
              Begin the ritual
              <ArrowRight size={14} strokeWidth={1.5} />
            </button>
            <span className="label-caps text-foreground/50">
              4 questions · 30 seconds
            </span>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.2 }}
          className="mt-20 lg:mt-0 flex items-center justify-between text-foreground/40"
        >
          <span className="label-caps">For the fragrance community</span>
          <span className="label-caps hidden sm:inline">MMXXVI</span>
        </motion.div>
      </div>

      {/* Right column — image + discovery card */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1.4, delay: 0.2 }}
        className="lg:col-span-5 relative min-h-[40vh] lg:min-h-screen border-l border-border overflow-hidden flex flex-col"
      >
        <div className="relative flex-1 min-h-[40vh]">
          <img
            src={HERO_IMG}
            alt="Perfume bottle in shadow"
            className="w-full h-full object-cover absolute inset-0"
            style={{ filter: "grayscale(0.15) contrast(1.05)" }}
          />
          <div className="absolute inset-0 bg-background/20" />
          <div className="absolute bottom-8 right-8 label-caps text-foreground/70 mix-blend-difference">
            Nº 001 · Ébène
          </div>
        </div>
        <div className="border-t border-border p-4 sm:p-6 bg-background">
          <Discovery />
        </div>
      </motion.div>
    </section>
  );
}
